import { FC, useId, useRef } from 'react';
import './BrandOnboardContent.scss';
import { BrandContentModel as model } from './model';
import { IBrandOnboardContent, TrackingEventType } from '../../../common/view-model';
import { ButtonComponent } from '../../UI/ButtonComponent/ButtonComponent';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/AppReducer';
import { buildTrackingBobPayload } from '../../../helpers/handleBobTracking';
import { handleTracking } from '../../../helpers/handelTracking';
interface IAccordionItemContent {
	id: string;
	title: string;
	handleSetActive: (id: string) => void;
	active: boolean;
	content: React.JSX.Element;
	userState: any;
}
const AccordionItemContent: FC<IAccordionItemContent> = ({
	id,
	title,
	handleSetActive,
	active,
	content,
	userState
}) => {
	const accordionClickableId = useId();
	const handleClick = (event: any) => {
		event.stopPropagation();
		if (active && event.target.id !== accordionClickableId) return;
		handleSetActive(id);
	};
	const trackCallback = () => {
		const trackingPayload = buildTrackingBobPayload({
			type: TrackingEventType.BOB_TRACKING_GOB_ACCORDION,
			user: {
				id: userState?.id || '',
				type: userState?.user_type || ''
			},
			event: {
				id: `click-accordion-${id}`,
				name: `click-accordion-${id}`,
				type: `click-accordion`
			}
		});
		if (!active) {
			handleTracking(trackingPayload);
		}
	};
	return (
		<div
			id={id}
			className={`accordion-item-container ${!active ? 'clickable' : ''}`}
			onClick={event => {
				trackCallback();
				handleClick(event);
			}}
		>
			<div className='accordion-title-container'>
				<span id={accordionClickableId} className={`accordion-title ${active ? 'active' : ''}`}>
					{title}
				</span>
				<i
					id={accordionClickableId}
					className={`${
						active ? 'main-menu-close ri-subtract-line' : 'main-menu-open ri-add-line'
					}`}
				></i>
			</div>
			{active && <div className='accordion-content'>{content}</div>}
			<div className='separator'></div>
		</div>
	);
};

interface IOnboardContentCard {
	id: string;
	title: string;
	description: string;
	handleSetActive: (id: string) => void;
	activeElementId?: string;
	accordionContainerRef?: React.RefObject<HTMLDivElement | null>;
	userState: any;
}
const OnboardContentCard: FC<IOnboardContentCard> = ({
	id,
	title,
	description,
	handleSetActive,
	activeElementId,
	accordionContainerRef,
	userState
}) => {
	const handleCardClick = () => {
		if (activeElementId !== id) {
			handleSetActive(id);
		}
		accordionContainerRef?.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const trackCallback = () => {
		const trackingPayload = buildTrackingBobPayload({
			type: TrackingEventType.BOB_TRACKING_GOB_CARD,
			user: {
				id: userState?.id || '',
				type: userState?.user_type || ''
			},
			event: {
				id: `click-card-${id}`,
				name: `click-card-${id}`,
				type: `click-card`
			}
		});
		handleTracking(trackingPayload);
	};

	return (
		<div className='onboard-card-container'>
			<div className={`image-container ${id}`}></div>
			<div className='information-container'>
				<div className='name-container'>
					<span className='name'>{title}</span>
				</div>
				<div className='description-container'>
					<span className='description'>{description}</span>
				</div>
				<div className='button-container'>
					<ButtonComponent
						style='rounded-button secondary'
						onClick={() => {
							trackCallback();
							handleCardClick();
						}}
						text={model.btnText}
						icon={<i className='ri-arrow-right-s-line line-icon'></i>}
					/>
				</div>
			</div>
		</div>
	);
};

interface IBrandOnboardContentProps {
	elements: IBrandOnboardContent[];
	handelSetActive: (id: string) => void;
}
export const BrandOnboardContent: FC<IBrandOnboardContentProps> = ({
	elements,
	handelSetActive
}) => {
	const accordionContainerRef = useRef<HTMLDivElement>(null);
	const userState = useSelector((state: RootState) => state.appState.userState?.user);
	return (
		<div className='brand-onboard-content-container'>
			<div className='brand-onboard-title-container'>
				<span className='title'>{model.title}</span>
			</div>
			<div className='cards-container'>
				{elements.map(element => (
					<OnboardContentCard
						key={element.id}
						id={element.id}
						title={element.title}
						description={element.description}
						handleSetActive={handelSetActive}
						activeElementId={elements.find(element => element.active)?.id}
						accordionContainerRef={accordionContainerRef}
						userState={userState}
					/>
				))}
			</div>
			<div ref={accordionContainerRef} className='onboard-accordion-container'>
				{elements.map(element => (
					<AccordionItemContent
						key={element.id}
						id={element.id}
						title={element.title}
						active={element.active}
						handleSetActive={handelSetActive}
						content={element.content}
						userState={userState}
					/>
				))}
			</div>
		</div>
	);
};
